import PropTypes from "prop-types"
import React, { Component } from "react"
import Relay from "react-relay"
import classnames from "classnames"
import { find, includes, filter } from "lodash/collection"
import { startCase, camelCase, upperFirst } from "lodash/string"
import { FormattedMessage, injectIntl, FormattedHTMLMessage } from "react-intl"
import { dispatch } from "../../Dispatcher"
import SearchDisplayTools from "./SearchDisplayTools"
import FilterSuggest from "../elements/FilterSuggest"
import FilterDate from "../elements/FilterDate"
import FilterBreadcrumbs from "../elements/FilterBreadcrumbs"
import AddFilter from "../elements/AddFilter"
import AccordiTab from "../elements/AccordiTab/AccordiTab"
import AccordiItem from "../elements/AccordiTab/AccordiItem"
import CheckboxToggle from "../elements/CheckboxToggle"
import Icon from "../elements/Icon"
import { isKeyClick } from "../../scripts/utils"
import { filterableResourceTypes } from "../../scripts/resourceTypes"
import { licenseTypes as filterableLicenseTypes } from "../../scripts/licenseTypes"
import { FILTER_CONTROLS, SHOW_SEARCH_FILTERS } from "../../constants"
import {
  addSearchFilterControl,
  appendSearchFilter,
  removeSearchFilter
} from "../../actions"
import searchMessages from "../../messages/search"
import { messages as licenseMessages } from "../../messages/licenses"
import settingsMessages from "../../messages/settings"
import { isToko } from "../../scripts/maori"
import { getAccountSettings } from "../../scripts/accountSettings"
import { getStyles, getStyleTag } from "../../styles/dynamicStyles"
class SearchFilters extends Component {
  static defaultProps = {
    expanded: true,
    autoShowAt: 1024, //screen width above which filters should be displayed by default
    activeFilters: {}
  }

  constructor(props) {
    super(props)

    this.state = {
      articleStyle: this.props.articleStyle,
      articleStyles: this.props.articleStyles,
      toggleAllTypes: false,
      toggleAllLicenses: false
    }
  }

  componentDidMount() {
    const accountSettings = getAccountSettings(
      this.props.intl.formatMessage(settingsMessages.authenticAccountId)
    )
    const contentStyleType = accountSettings.settings.style.content

    document.body.classList.add("state-filters-initialised")
    if (document.body.getBoundingClientRect().width < this.props.autoShowAt) {
      dispatch({ type: SHOW_SEARCH_FILTERS, show: false })
    }
    if (contentStyleType !== "default") this.handleExpandFilters()
  }

  addFilterControl = evt => {
    dispatch(addSearchFilterControl(evt.target.value))
  }

  appendFilterValue = (value, name) => {
    this.setState({ [`suggest_${name}`]: "" })
    dispatch(
      appendSearchFilter({
        name,
        value
      })
    )
  }

  removeFilterValue = (value, name) => {
    dispatch(
      removeSearchFilter({
        name,
        value
      })
    )
  }

  getFacetOptions = (name, control) => {
    const { facets } = this.props

    const results =
      facets &&
      "facets" in facets.custom_fields &&
      name in facets.custom_fields.facets
        ? facets.custom_fields.facets[name]
        : []

    return results.map(({ value, count }) => {
      const dvalue =
        control && control.prepareDisplay
          ? control.prepareDisplay(value)
          : value
      return {
        label: dvalue,
        value: dvalue,
        count
      }
    })
  }

  getFilterValues = name => this.props.activeFilters[name] || []

  // unique markup for type filter control
  renderTypeFilterControl = () => {
    const { intl, facets, activeTypes } = this.props

    const types = filterableResourceTypes().reduce((filters, type) => {
      const facetType = find(facets && facets.type, { value: type.name })
      if (facetType && facetType.count > 0) {
        filters.push({
          name: type.name,
          label: intl.formatMessage({
            id: `type.${type.name}`,
            defaultMessage: type.label
          }),
          icon: facetType.icon,
          icon_color: facetType.icon_color,
          count: intl.formatNumber(facetType.count),
          checked: includes(activeTypes, type.name)
        })
      }
      return filters
    }, [])
    const typesActive = filter(types, { checked: true }).length
    const typesFilterStatusText =
      typesActive && types.length !== typesActive
        ? intl.formatMessage(searchMessages.filterStatusOf, {
            first: typesActive,
            last: types.length
          })
        : intl.formatMessage(searchMessages.filterStatusAll)

    return (
      <AccordiItem
        key={"type"}
        title={
          <span
            className="c-results-filters__head"
            style={getStyles("c-results-filters__head")}
          >
            <FormattedMessage {...searchMessages.typeOfContent} />
            <small>{typesFilterStatusText}</small>
          </span>
        }
        expandedClassName="type-filters-exanded"
        className={classnames({
          "state-filter-active": typesActive > 0,
          "state-filter-inactive": typesActive < 1
        })}
        id="type-filters"
      >
        <ul
          className="c-results-filters__list menu vertical nested"
          style={getStyles("c-results-filters__list")}
        >
          {types.map(type => (
            <li
              key={type.name}
              className="c-results-filters__filter"
              style={getStyles("c-results-filters__filter")}
            >
              <CheckboxToggle
                value={type.name}
                handleClick={e => this.handleFilterType(e, type)}
                checked={type.checked}
              >
                <Icon icon={type.icon} color={type.icon_color} />
                {type.label} <small>({type.count})</small>
              </CheckboxToggle>
            </li>
          ))}
          <li
            className="c-results-filters__filter c-results-filters__filter--all"
            style={getStyles([
              "c-results-filters__filter",
              "c-results-filters__filter--all"
            ])}
          >
            <CheckboxToggle
              checked={this.state.toggleAllTypes}
              handleClick={this.handleToggleTypes}
            >
              <FormattedMessage {...searchMessages.toggleAll} />
            </CheckboxToggle>
          </li>
        </ul>
      </AccordiItem>
    )
  }

  // unique markup for license filter control
  renderLicenseFilterControl = () => {
    const { intl, facets, activeLicenses } = this.props
    const {
      settings: { contact_email }
    } = getAccountSettings(
      intl.formatMessage(settingsMessages.authenticAccountId)
    )

    const licenseFromCustomFields =
      facets &&
      facets.custom_fields &&
      find(facets.custom_fields, { attribute: "license" })
    const facetsFromCustomFields =
      !!licenseFromCustomFields &&
      licenseFromCustomFields.facets &&
      !!licenseFromCustomFields.facets.length
        ? licenseFromCustomFields.facets
        : []

    // Filter on these licenses, merge in count ( from facets ) and checked
    const licenses = filterableLicenseTypes.reduce((filters, license) => {
      const facetCount = find(facetsFromCustomFields, { value: license.name })

      if (facetCount && facetCount.count > 0) {
        filters.push({
          ...license,
          label: intl.formatMessage(licenseMessages[license.label], {
            email: contact_email
          }),
          count: facetCount ? facetCount.count : 0,
          checked: includes(activeLicenses, license.name)
        })
      }
      return filters
    }, [])
    const licensesActive = filter(licenses, { checked: true }).length
    const licensesFilterStatusText =
      licensesActive && licenses.length !== licensesActive
        ? intl.formatMessage(searchMessages.filterStatusOf, {
            first: licensesActive,
            last: licenses.length
          })
        : intl.formatMessage(searchMessages.filterStatusAll)

    return (
      <AccordiItem
        key={"license"}
        title={
          <span
            className="c-results-filters__head"
            style={getStyles("c-results-filters__head")}
          >
            <FormattedMessage
              {...searchMessages.licenseStatusText}
              values={{
                statusText: <small>{licensesFilterStatusText}</small>
              }}
            />
          </span>
        }
        expandedClassName="license-filters-exanded"
        className={classnames({
          "state-filter-active": licensesActive > 0,
          "state-filter-inactive": licensesActive < 1
        })}
        id="license-filters"
      >
        <ul
          className="c-results-filters__list menu vertical nested"
          style={getStyles("c-results-filters__list")}
        >
          {licenses.map(license => (
            <li
              key={license.name}
              className="c-results-filters__filter"
              style={getStyles("c-results-filters__filter")}
            >
              <CheckboxToggle
                handleClick={e => this.handleFilterLicense(e, license)}
                checked={license.checked}
              >
                {license.label} <small>({license.count})</small>
              </CheckboxToggle>
            </li>
          ))}
          <li
            className="c-results-filters__filter c-results-filters__filter--all"
            style={getStyles([
              "c-results-filters__filter",
              "c-results-filters__filter--all"
            ])}
          >
            <CheckboxToggle
              checked={this.state.toggleAllLicenses}
              handleClick={this.handleToggleLicense}
              style={getStyles("c-results-filters__filter__label")}
            >
              <FormattedMessage {...searchMessages.toggleAll} />
            </CheckboxToggle>
          </li>
        </ul>
      </AccordiItem>
    )
  }

  // unique markup for tag filter control
  renderTagFilterControl = () => {
    const { intl, activeTags } = this.props
    const toko = isToko(activeTags.length)

    return (
      <AccordiItem
        key={"tags"}
        title={
          <span
            href="#"
            className="c-results-filters__head"
            style={getStyles("c-results-filters__head")}
          >
            {toko && (
              <FormattedHTMLMessage
                {...searchMessages.taggedWith}
                values={{ tagCount: activeTags.length }}
              />
            )}
            {!toko && (
              <FormattedHTMLMessage
                {...searchMessages.taggedWithE}
                values={{ tagCount: activeTags.length }}
              />
            )}
          </span>
        }
        expandedClassName="tag-filters-exanded"
        className={classnames({
          "state-filter-active": activeTags.length > 0,
          "state-filter-inactive": activeTags.length < 1
        })}
        id="tag-filters"
        expanded={activeTags.length > 0}
      >
        <FilterSuggest
          searchValue={this.state.tag}
          name="subject_and_tags"
          suggestions={this.getFacetOptions("subject_and_tags")}
          activeOptions={activeTags}
          placeholder={intl.formatMessage(searchMessages.filterTagPlaceholder)}
          onDeleteOption={this.handleTagDelete}
          onSelectOption={this.handleTagSelect}
          onChange={tag => this.setState({ tag })}
        />
      </AccordiItem>
    )
  }

  renderFilterControl = ({ name, type, id }) => {
    const { filterConfiguration, breadcrumbsSearch, dates } = this.props
    const control = FILTER_CONTROLS.find(filter => filter.name === name) || {
      name,
      type,
      id
    }

    // if one of the unique filter controls, render it
    if (name === "type") {
      return this.renderTypeFilterControl()
    }
    if (name === "license") {
      return this.renderLicenseFilterControl()
    }
    if (name === "tags") {
      return this.renderTagFilterControl()
    }

    switch (type) {
      case "float":
      case "html":
      case "integer":
      case "string":
      case "nested_item":
        return (
          <AccordiItem
            key={name}
            expandedClassName={`tag-${name}-exanded`}
            id={name}
            title={
              <span
                href="#"
                className="c-results-filters__head"
                style={getStyles("c-results-filters__head")}
              >
                {startCase(name)}
              </span>
            }
          >
            <FilterSuggest
              name={name}
              searchValue={this.state[`suggest_${name}`] || ""}
              suggestions={this.getFacetOptions(name, control)}
              activeOptions={this.getFilterValues(name)}
              onDeleteOption={value => this.removeFilterValue(value, name)}
              onSelectOption={value => this.appendFilterValue(value, name)}
              onChange={searchValue =>
                this.setState({
                  [`suggest_${name}`]: searchValue
                })
              }
            />
          </AccordiItem>
        )
      case "date":
      case "edtf":
        const dateFrom = dates && dates[name] ? dates[name].from : null
        const dateTo = dates && dates[name] ? dates[name].to : null

        return (
          <FilterDate
            key={name}
            name={name}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        )
      case "breadcrumbs":
        return (
          <AccordiItem
            key={name}
            expandedClassName={`tag-${name}-exanded`}
            id={name}
            title={
              <span
                href="#"
                className="c-results-filters__head"
                style={getStyles("c-results-filters__head")}
              >
                {startCase(name)}
              </span>
            }
          >
            <FilterBreadcrumbs
              name={name}
              filter={filterConfiguration[name]}
              breadcrumbsSearch={breadcrumbsSearch[name]}
            />
          </AccordiItem>
        )
      default:
        return null
    }
  }

  handleFilterType = (e, type) => {
    dispatch({
      type: "search-filter-type",
      data: { type: type.name, active: e.target.checked }
    })
  }

  handleFilterLicense = (e, license) => {
    dispatch({
      type: "search-filter-license",
      data: { type: license.name, active: e.target.checked }
    })
  }

  handleToggleLicense = e => {
    this.setState({ toggleAllLicenses: e.target.checked })
    dispatch({ type: "search-filters-license-toggle", value: e.target.checked })
  }

  handleToggleTypes = e => {
    this.setState({ toggleAllTypes: e.target.checked })
    dispatch({ type: "search-filters-type-toggle", value: e.target.checked })
  }

  handleTagSelect = tag => {
    this.setState({
      tag: ""
    })
    dispatch({ type: "search-filter-add-tag", tag: tag })
  }

  handleTagDelete = tag => {
    dispatch({ type: "search-filter-remove-tag", tag: tag })
  }

  handleExpandFilters = () => {
    dispatch({ type: "toggle-search-filters" })
  }

  render() {
    const { expanded, filterConfiguration, filterControls, intl } = this.props
    const { activeItems, articleStyles, articleStyle } = this.state

    const accountSettings = getAccountSettings(
      intl.formatMessage(settingsMessages.authenticAccountId)
    )
    const contentStyleType = accountSettings.settings.style.content

    // create array of all filter controls
    const mappedAllFilterControls = Object.entries(filterConfiguration).map(
      ([key, value]) => ({
        name: key,
        type: value.type,
        id: `fieldName${upperFirst(camelCase(key))}`
      })
    )
    // get primary filter controls
    const primaryFilterControls = Object.fromEntries(
      Object.entries(filterConfiguration).filter(
        ([_, value]) => value.public_position_in_filter === "primary_filter"
      )
    )
    // create array of primary filter controls
    let mappedPrimaryFilterControls = Object.entries(primaryFilterControls).map(
      ([key, value]) => ({
        name: key,
        type: value.type || "string",
        id: `fieldName${upperFirst(camelCase(key))}`
      })
    )

    // add filter controls to primary filter controls on ADD_SEARCH_FILTER_CONTROL action
    if (filterControls.length && Object.keys(filterConfiguration).length) {
      mappedPrimaryFilterControls = [
        ...mappedPrimaryFilterControls,
        ...filterControls.map(filterControl => {
          return {
            name: filterControl,
            type: filterConfiguration[filterControl].type || "string",
            id: `fieldName${upperFirst(camelCase(filterControl))}`
          }
        })
      ]
    }

    // create array of unique filter options to 'add filter' dropdown
    const uniqueFilterOptions = mappedAllFilterControls.reduce((acc, curr) => {
      const filterName = curr.name

      if (filterControls.includes(filterName)) {
        return acc
      }

      if (
        filterConfiguration[filterName] &&
        filterConfiguration[filterName].public_position_in_filter ===
          "no_filter"
      ) {
        return acc
      }

      if (primaryFilterControls[filterName]) {
        return acc
      }

      const isExist = acc.find(filter => filter.name === filterName)

      if (isExist) {
        return acc
      }

      acc.push(curr)

      return acc
    }, [])

    return (
      <div
        className="c-results-filters search-results__filters js-results-filters"
        aria-label={intl.formatMessage(searchMessages.searchResultFilters)}
        style={getStyles(["search-results__filters", "c-results-filters"])}
      >
        {contentStyleType === "type1" && (
          <span
            className="expanded-filter-hidden"
            style={getStyles("search-settings__text")}
          >
            Search settings
          </span>
        )}
        {getStyleTag([
          "date-input--input",
          "c-results-filters__filter--all",
          "date-input--action__button",
          "c-results-filters__toggle",
          "button--alert-subtle",
          "c-results-filters__head"
        ])}
        <SearchDisplayTools
          articleStyles={articleStyles}
          articleStyle={articleStyle}
        />
        {contentStyleType === "default" ? (
          <h4
            id="search-filters-title"
            className="c-results-filters__title"
            aria-controls="search-filters"
            aria-expanded={expanded}
            tabIndex="0"
            onClick={e => {
              this.handleExpandFilters()
            }}
            onKeyPress={e => {
              if (isKeyClick(e)) {
                this.handleExpandFilters()
              }
            }}
          >
            <span className="c-results-filters__message">
              <FormattedMessage {...searchMessages.narrowSearchResults} />
            </span>
            {/*<span className="hide-for-large"><span className="show-for-medium"><FormattedHTMLMessage {...searchMessages.numberSearchResults} /></span></span>*/}
            <span
              className="c-results-filters__toggle"
              style={getStyles("c-results-filters__toggle")}
            >
              <span className="c-results-filters__toggle-hide">
                <Icon icon={"filter"} extraClasses="icon--1_5x" />
                <FormattedMessage {...searchMessages.hide} />
              </span>
              <span className="c-results-filters__toggle-show">
                <Icon icon={"filter"} extraClasses="icon--1_5x" />
                <FormattedMessage {...searchMessages.show} />
              </span>
            </span>
          </h4>
        ) : (
          <div
            className="c-results-filters__toggle"
            style={getStyles("c-results-filters__toggle")}
            onClick={e => {
              this.handleExpandFilters()
            }}
            onKeyPress={e => {
              if (isKeyClick(e)) {
                this.handleExpandFilters()
              }
            }}
          >
            <span className="c-results-filters__toggle-hide">
              <Icon icon={"cross"} />
            </span>
            {contentStyleType === "type1" && (
              <span
                className="expanded-filter-hidden"
                style={getStyles("c-results-filters__text")}
              >
                Filters
              </span>
            )}
            <span className="c-results-filters__toggle-show">
              <Icon icon={"filter"} extraClasses="icon--1_5x" />
            </span>
          </div>
        )}
        <form
          id="search-filters"
          className="c-results-filters__cont js-results-filters-content"
          style={getStyles("c-results-filters__cont")}
          aria-hidden={expanded ? "false" : "true"}
          aria-labelledby="search-filters-title"
          onSubmit={e => e.preventDefault()}
        >
          <AccordiTab
            className="c-results-filters__accordian"
            style={getStyles("c-results-filters__accordian")}
            allowMultiple={true}
            activeItems={activeItems}
          >
            {mappedPrimaryFilterControls.map(({ name, type, id }) =>
              this.renderFilterControl({ name, type, id })
            )}
          </AccordiTab>

          <div
            className="c-results-filters__footer"
            style={getStyles("c-results-filters__footer")}
          >
            <AddFilter
              options={uniqueFilterOptions}
              onChange={this.addFilterControl}
            />
          </div>
        </form>
      </div>
    )
  }
}

SearchFilters.propTypes = {
  expanded: PropTypes.bool,
  filterControls: PropTypes.array.isRequired,
  autoShowAt: PropTypes.number,
  activeFilters: PropTypes.object
}

export default Relay.createContainer(injectIntl(SearchFilters), {
  fragments: {
    facets: () => Relay.QL`
            fragment on Facet {
                type {
                  count
                  value
                  icon
                  icon_color
                }
                custom_fields {
                  attribute
                  facets {
                    value
                    count
                  }
                }
            }
        `
  }
})



// WEBPACK FOOTER //
// ./webapp/components/modules/SearchFilters.js